import type { Bar } from '../core/types'
import {
  type Account,
  type AccountState,
  type Direction,
  type InstrumentSpec,
  type Position,
  type Side,
  type SimConfig,
  type SimEvent,
  type Trade,
  DEFAULT_SIM_CONFIG,
} from './types'
import { getInstrument } from './instruments'

// Synchronous, deterministic trading simulator. No persistence (the controller
// flushes closed trades to the journal). Netting: one position per instrument.
//
// Fill model (MVP, FT-style):
//   - Market entry / manual close fill instantly at the current bar close,
//     with spread (bid/ask = mid -/+ spread/2) and optional slippage.
//   - TP/SL are evaluated each new bar against the bar high/low at the level
//     price (spread not re-applied), with gap fills at the bar open.

export interface SimEngineOpts {
  account: { currency?: string; startingBalance: number }
  instruments?: Record<string, InstrumentSpec>
  config?: Partial<SimConfig>
  idGen?: () => string
}

function roundLot(size: number, step: number): number {
  return Math.round(size / step) * step
}

export class SimEngine {
  private account: Account
  private readonly config: SimConfig
  private readonly specs: (symbol: string) => InstrumentSpec
  private readonly positions = new Map<string, Position>()
  private readonly currentBars = new Map<string, Bar>()
  private seq = 0
  private readonly idGen: () => string

  constructor(opts: SimEngineOpts) {
    this.account = {
      currency: opts.account.currency ?? 'USD',
      startingBalance: opts.account.startingBalance,
      balance: opts.account.startingBalance,
    }
    this.config = { ...DEFAULT_SIM_CONFIG, ...opts.config }
    const table = opts.instruments
    this.specs = table ? (s) => table[s] ?? getInstrument(s) : getInstrument
    this.idGen = opts.idGen ?? (() => `t${++this.seq}`)
  }

  // ---- inputs from the replay engine ----

  /** Called for every revealed bar. Processes TP/SL, returns resulting events. */
  onNewBar(symbol: string, bar: Bar): SimEvent[] {
    this.currentBars.set(symbol, bar)
    const pos = this.positions.get(symbol)
    if (!pos) return []
    const hit = this.checkTpSl(pos, bar)
    if (!hit) return []
    const trade = this.realize(pos, hit.price, bar.time, hit.reason, pos.size, pos.entryCommission)
    this.positions.delete(symbol)
    this.account.balance = round2(this.account.balance + trade.netPnL)
    return [{ kind: 'close', trade }]
  }

  // ---- inputs from the UI ----

  placeMarket(
    symbol: string,
    side: Side,
    size: number,
    levels: { tp?: number | null; sl?: number | null } = {},
  ): SimEvent[] {
    const spec = this.specs(symbol)
    const bar = this.currentBars.get(symbol)
    if (!bar) return [{ kind: 'reject', reason: 'no current bar' }]
    let lots = roundLot(size, spec.lotStep)
    if (lots < spec.minLot) return [{ kind: 'reject', reason: `size below min lot ${spec.minLot}` }]

    const fill = this.marketFillPrice(spec, side, bar.close)
    const orderDir: Direction = side === 'buy' ? 'long' : 'short'
    const events: SimEvent[] = [
      { kind: 'fill', symbol, side, price: fill, time: bar.time, size: lots },
    ]
    const commission = spec.commissionPerLotPerSide

    const pos = this.positions.get(symbol)
    if (!pos) {
      this.openPosition(symbol, orderDir, lots, fill, bar.time, levels, spec)
      return events
    }

    if (pos.direction === orderDir) {
      // scale in -> weighted average
      const newSize = round4(pos.size + lots)
      pos.avgEntryPrice = (pos.avgEntryPrice * pos.size + fill * lots) / newSize
      pos.size = newSize
      pos.entryCommission = round2(pos.entryCommission + commission * lots)
      if (levels.tp !== undefined) pos.tp = levels.tp
      if (levels.sl !== undefined) pos.sl = levels.sl
      pos.initialSl = pos.sl
      pos.initialRisk = riskOf(pos.avgEntryPrice, pos.sl, spec, pos.size)
      return events
    }

    // opposite direction -> reduce / close / flip
    const closeSize = Math.min(lots, pos.size)
    const entryCommissionPortion = round2(pos.entryCommission * (closeSize / pos.size))
    const exitCommission = commission * closeSize
    const trade = this.realize(
      pos,
      fill,
      bar.time,
      lots > pos.size ? 'reverse' : 'manual', // flip leaves remaining -> reverse; exact = manual close
      closeSize,
      entryCommissionPortion + exitCommission,
    )
    this.account.balance = round2(this.account.balance + trade.netPnL)
    events.push({ kind: 'close', trade })

    pos.size = round4(pos.size - closeSize)
    pos.entryCommission = round2(pos.entryCommission - entryCommissionPortion)
    if (pos.size <= 1e-9) this.positions.delete(symbol)

    const remaining = round4(lots - closeSize)
    if (remaining > 0) {
      this.openPosition(symbol, orderDir, remaining, fill, bar.time, levels, spec, commission)
    }
    return events
  }

  /** Manual full close of the open position at the current bar. */
  closePosition(symbol: string): SimEvent[] {
    const pos = this.positions.get(symbol)
    if (!pos) return [{ kind: 'reject', reason: 'no open position' }]
    const side: Side = pos.direction === 'long' ? 'sell' : 'buy'
    return this.placeMarket(symbol, side, pos.size)
  }

  // ---- reads ----

  getPosition(symbol: string): Position | null {
    return this.positions.get(symbol) ?? null
  }

  getAccountState(symbol?: string): AccountState {
    let unrealized = 0
    for (const pos of this.positions.values()) {
      const bar = this.currentBars.get(pos.symbol)
      if (!bar) continue
      if (symbol && pos.symbol !== symbol) continue
      unrealized += this.unrealizedPnL(pos, bar.close)
    }
    unrealized = round2(unrealized)
    return {
      balance: this.account.balance,
      unrealized,
      equity: round2(this.account.balance + unrealized),
    }
  }

  // ---- internals ----

  private openPosition(
    symbol: string,
    direction: Direction,
    size: number,
    price: number,
    time: number,
    levels: { tp?: number | null; sl?: number | null },
    spec: InstrumentSpec,
    commissionPerLot = spec.commissionPerLotPerSide,
  ): void {
    const sl = levels.sl ?? null
    this.positions.set(symbol, {
      id: this.idGen(),
      symbol,
      direction,
      size,
      avgEntryPrice: price,
      entryTime: time,
      tp: levels.tp ?? null,
      sl,
      initialSl: sl,
      initialRisk: riskOf(price, sl, spec, size),
      entryCommission: round2(commissionPerLot * size),
    })
  }

  private marketFillPrice(spec: InstrumentSpec, side: Side, mid: number): number {
    const half = spec.spreadPoints / 2
    const slip = spec.slippagePoints
    const raw = side === 'buy' ? mid + half + slip : mid - half - slip
    return roundPrice(raw, spec.priceStep)
  }

  private checkTpSl(pos: Position, bar: Bar): { price: number; reason: 'tp' | 'sl' } | null {
    const { tp, sl, direction } = pos
    let slHit = false
    let tpHit = false
    if (direction === 'long') {
      slHit = sl !== null && bar.low <= sl
      tpHit = tp !== null && bar.high >= tp
    } else {
      slHit = sl !== null && bar.high >= sl
      tpHit = tp !== null && bar.low <= tp
    }
    if (!slHit && !tpHit) return null
    const pickSl = slHit && (!tpHit || this.config.ambiguousBar === 'pessimistic')
    if (pickSl) return { price: this.exitFillPrice(pos, sl!, 'sl', bar), reason: 'sl' }
    return { price: this.exitFillPrice(pos, tp!, 'tp', bar), reason: 'tp' }
  }

  private exitFillPrice(pos: Position, level: number, reason: 'tp' | 'sl', bar: Bar): number {
    // Gap handling: if the bar opened beyond the level, fill at the open.
    if (reason === 'sl') {
      if (pos.direction === 'long') return bar.open < level ? bar.open : level
      return bar.open > level ? bar.open : level
    }
    // tp
    if (pos.direction === 'long') return bar.open > level ? bar.open : level
    return bar.open < level ? bar.open : level
  }

  private realize(
    pos: Position,
    exitPrice: number,
    exitTime: number,
    reason: Trade['exitReason'],
    size: number,
    commission: number,
  ): Trade {
    const spec = this.specs(pos.symbol)
    const diff = pos.direction === 'long' ? exitPrice - pos.avgEntryPrice : pos.avgEntryPrice - exitPrice
    const grossPnL = round2(diff * spec.valuePerPointPerLot * size)
    const netPnL = round2(grossPnL - commission)
    const riskPortion =
      pos.initialRisk !== null ? pos.initialRisk * (size / pos.size) : null
    const rMultiple = riskPortion && riskPortion > 0 ? round2(netPnL / riskPortion) : null
    return {
      id: pos.id,
      symbol: pos.symbol,
      direction: pos.direction,
      size,
      entryTime: pos.entryTime,
      entryPrice: round4(pos.avgEntryPrice),
      exitTime,
      exitPrice: round4(exitPrice),
      tp: pos.tp,
      sl: pos.sl,
      initialSl: pos.initialSl,
      grossPnL,
      commission: round2(commission),
      netPnL,
      rMultiple,
      exitReason: reason,
    }
  }

  private unrealizedPnL(pos: Position, mid: number): number {
    const spec = this.specs(pos.symbol)
    const diff = pos.direction === 'long' ? mid - pos.avgEntryPrice : pos.avgEntryPrice - mid
    return diff * spec.valuePerPointPerLot * pos.size
  }
}

function riskOf(entry: number, sl: number | null, spec: InstrumentSpec, size: number): number | null {
  if (sl === null) return null
  return round2(Math.abs(entry - sl) * spec.valuePerPointPerLot * size)
}

function roundPrice(p: number, step: number): number {
  if (!step) return p
  return Math.round(p / step) * step
}
function round2(n: number): number {
  return Math.round(n * 100) / 100
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000
}
